import React, { useState } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import RadioQuestion from "../../components/RadioQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { YES_NO_OPTIONS } from "../../constants";
import Image from "../../components/Image";
import imageCatAndHousePlant from "../../images/cat-and-houseplant.png";

const defaultErrorMessages = {
  cashAndSavings: "Please choose an option",
  mortgage: "Please choose an option",
  sellingAnotherProperty: "Please choose an option",
  remortgagingAnotherProperty: "Please choose an option",
  helpToBuyIsa: "Please choose an option",
  inheritance: "Please choose an option",
  lifetimeIsa: "Please choose an option",
  someoneGivingMeMoney: "Please choose an option",
  someoneLendingMeMoney: "Please choose an option",
  bonusFromEmployer: "Please choose an option",
  trust: "Please choose an option",
};

const HowIsBuyerPaying = ({ howIsBuyerPaying, onStepComplete }) => {
  const [formState, setFormState] = useState({
    cashAndSavings: howIsBuyerPaying?.cashAndSavings ?? null,
    mortgage: howIsBuyerPaying?.mortgage ?? null,
    sellingAnotherProperty: howIsBuyerPaying?.sellingAnotherProperty ?? null,
    remortgagingAnotherProperty: howIsBuyerPaying?.remortgagingAnotherProperty ?? null,
    helpToBuyIsa: howIsBuyerPaying?.helpToBuyIsa ?? null,
    lifetimeIsa: howIsBuyerPaying?.lifetimeIsa ?? null,
    inheritance: howIsBuyerPaying?.inheritance ?? null,
    someoneGivingMeMoney: howIsBuyerPaying?.someoneGivingMeMoney ?? null,
    someoneLendingMeMoney: howIsBuyerPaying?.someoneLendingMeMoney ?? null,
    bonusFromEmployer: howIsBuyerPaying?.bonusFromEmployer ?? null,
    trust: howIsBuyerPaying?.trust ?? null,
  });
  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({ ...defaultErrorMessages });

  const handleInputChange = (field) => (value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleValidation = () => {
    let newErrors = {};
    let newErrorMessages = { ...errorMessages };
    let formIsValid = true;

    if (formState.cashAndSavings === null) {
      newErrors.cashAndSavings = true;
      formIsValid = false;
    }

    if (formState.mortgage === null) {
      newErrors.mortgage = true;
      formIsValid = false;
    }

    if (formState.sellingAnotherProperty === null) {
      newErrors.sellingAnotherProperty = true;
      formIsValid = false;
    }

    if (formState.remortgagingAnotherProperty === null) {
      newErrors.remortgagingAnotherProperty = true;
      formIsValid = false;
    }

    if (formState.helpToBuyIsa === null) {
      newErrors.helpToBuyIsa = true;
      formIsValid = false;
    }

    if (formState.lifetimeIsa === null) {
      newErrors.lifetimeIsa = true;
      formIsValid = false;
    }

    if (formState.inheritance === null) {
      newErrors.inheritance = true;
      formIsValid = false;
    }

    if (formState.someoneGivingMeMoney === null) {
      newErrors.someoneGivingMeMoney = true;
      formIsValid = false;
    }

    if (formState.someoneLendingMeMoney === null) {
      newErrors.someoneLendingMeMoney = true;
      formIsValid = false;
    }

    if (formState.bonusFromEmployer === null) {
      newErrors.bonusFromEmployer = true;
      formIsValid = false;
    }

    if (formState.trust === null) {
      newErrors.trust = true;
      formIsValid = false;
    }

    // You can't answer 'No' to all questions
    if (
      formState.cashAndSavings === false &&
      formState.mortgage === false &&
      formState.sellingAnotherProperty === false &&
      formState.remortgagingAnotherProperty === false &&
      formState.helpToBuyIsa === false &&
      formState.lifetimeIsa === false &&
      formState.inheritance === false &&
      formState.someoneGivingMeMoney === false &&
      formState.someoneLendingMeMoney === false &&
      formState.bonusFromEmployer === false &&
      formState.trust === false
    ) {
      // Show an error on the first field
      newErrors.cashAndSavings = true;
      newErrorMessages.cashAndSavings = "Please let us know how you will pay for the property";
      formIsValid = false;
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
    return formIsValid;
  };

  const handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field] && formState[field] !== false) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const saveHowIsBuyerPaying = (event) => {
    event.preventDefault();

    if (handleValidation()) {
      onStepComplete({
        howIsBuyerPaying: {
          cashAndSavings: formState.cashAndSavings,
          mortgage: formState.mortgage,
          sellingAnotherProperty: formState.sellingAnotherProperty,
          remortgagingAnotherProperty: formState.remortgagingAnotherProperty,
          helpToBuyIsa: formState.helpToBuyIsa,
          lifetimeIsa: formState.lifetimeIsa,
          inheritance: formState.inheritance,
          someoneGivingMeMoney: formState.someoneGivingMeMoney,
          someoneLendingMeMoney: formState.someoneLendingMeMoney,
          bonusFromEmployer: formState.bonusFromEmployer,
          trust: formState.trust,
        },
      });
    }
  };

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title="What will you use to pay for your property?" removeTopPadding>
        <p>
          Tell us about money you are using to pay for your new home, stamp duty, and any fees (including Juno's legal
          fee).
        </p>
        <p>If you're buying the property with someone else, we'll need to know about their money too.</p>

        <RadioQuestion
          question="Are you using savings or investments towards your purchase?"
          hint="This is separate from any savings in Help to buy ISAs or Lifetime ISAs"
          options={YES_NO_OPTIONS}
          inline={true}
          name="cashAndSavings"
          selected={formState.cashAndSavings}
          optionSelected={handleInputChange("cashAndSavings")}
          validate={handleFieldValidation("cashAndSavings")}
          validationError={errors.cashAndSavings ? errorMessages.cashAndSavings : null}
        />

        <RadioQuestion
          question="Are you taking out a mortgage on your new home?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="mortgage"
          selected={formState.mortgage}
          optionSelected={handleInputChange("mortgage")}
          validate={handleFieldValidation("mortgage")}
          validationError={errors.mortgage ? errorMessages.mortgage : null}
        />

        <RadioQuestion
          question="Will you be using the money from the sale of another property?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="sellingAnotherProperty"
          selected={formState.sellingAnotherProperty}
          optionSelected={handleInputChange("sellingAnotherProperty")}
          validate={handleFieldValidation("sellingAnotherProperty")}
          validationError={errors.sellingAnotherProperty ? errorMessages.sellingAnotherProperty : null}
        />

        <RadioQuestion
          question="Will you be using the money from the remortgage of another property?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="remortgagingAnotherProperty"
          selected={formState.remortgagingAnotherProperty}
          optionSelected={handleInputChange("remortgagingAnotherProperty")}
          validate={handleFieldValidation("remortgagingAnotherProperty")}
          validationError={errors.remortgagingAnotherProperty ? errorMessages.remortgagingAnotherProperty : null}
        />

        <RadioQuestion
          question="Are you using money saved in a Help to Buy ISA for this purchase?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="helpToBuyIsa"
          selected={formState.helpToBuyIsa}
          optionSelected={handleInputChange("helpToBuyIsa")}
          validate={handleFieldValidation("helpToBuyIsa")}
          validationError={errors.helpToBuyIsa ? errorMessages.helpToBuyIsa : null}
        />

        <RadioQuestion
          question="Are you using money saved in a Lifetime ISA to pay for this purchase?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="lifetimeIsa"
          selected={formState.lifetimeIsa}
          optionSelected={handleInputChange("lifetimeIsa")}
          validate={handleFieldValidation("lifetimeIsa")}
          validationError={errors.lifetimeIsa ? errorMessages.lifetimeIsa : null}
        />

        <RadioQuestion
          question="Are you using an inheritance towards your purchase?"
          hint="A gift of money from someone who has died"
          options={YES_NO_OPTIONS}
          inline={true}
          name="inheritance"
          selected={formState.inheritance}
          optionSelected={handleInputChange("inheritance")}
          validate={handleFieldValidation("inheritance")}
          validationError={errors.inheritance ? errorMessages.inheritance : null}
        />

        <RadioQuestion
          question="Is anyone giving you, or has anyone given you over the last 6 months, money you are using towards your purchase?"
          hint="A gift of money from someone who is alive"
          options={YES_NO_OPTIONS}
          inline={true}
          name="someoneGivingMeMoney"
          selected={formState.someoneGivingMeMoney}
          optionSelected={handleInputChange("someoneGivingMeMoney")}
          validate={handleFieldValidation("someoneGivingMeMoney")}
          validationError={errors.someoneGivingMeMoney ? errorMessages.someoneGivingMeMoney : null}
        />

        <RadioQuestion
          question="Are you getting a loan (not including a mortgage) from a person or bank to help pay for your purchase?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="someoneLendingMeMoney"
          selected={formState.someoneLendingMeMoney}
          optionSelected={handleInputChange("someoneLendingMeMoney")}
          validate={handleFieldValidation("someoneLendingMeMoney")}
          validationError={errors.someoneLendingMeMoney ? errorMessages.someoneLendingMeMoney : null}
        />

        <RadioQuestion
          question="Are you using an annual or one-off bonus from your employer?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="bonusFromEmployer"
          selected={formState.bonusFromEmployer}
          optionSelected={handleInputChange("bonusFromEmployer")}
          validate={handleFieldValidation("bonusFromEmployer")}
          validationError={errors.bonusFromEmployer ? errorMessages.bonusFromEmployer : null}
        />

        <RadioQuestion
          question="Are you using money from a Trust?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="trust"
          selected={formState.trust}
          optionSelected={handleInputChange("trust")}
          validate={handleFieldValidation("trust")}
          validationError={errors.trust ? errorMessages.trust : null}
        />

        <Action onClick={saveHowIsBuyerPaying}>Continue</Action>
      </Card>
      <Image src={imageCatAndHousePlant} />
    </React.Fragment>
  );
};

export default HowIsBuyerPaying;
